import React, { useState } from "react";
import { Package, X, Tag, CheckCircle } from "lucide-react";
import { Order } from "../../../../redux/cartLoadingSlice";
import './index.css'
import Swal from 'sweetalert2';

interface OrderSelectionProps {
  orders: Order[];
  onProceedToGroupedOrders: (orderGroups: any[]) => void;
  onBackToCartList: () => void;
  cartId: string;
}

interface OrderGroup {
  id: string;
  name: string;
  orders: string[];
  color: string;
}

const OrderSelection: React.FC<OrderSelectionProps> = ({
  orders,
  onProceedToGroupedOrders,
  onBackToCartList,
  cartId,
}) => {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [orderGroups, setOrderGroups] = useState<OrderGroup[]>([]);
  const [newGroupName, setNewGroupName] = useState("");

  // Safe customer display name getter
  const getCustomerDisplayName = (order: Order): string => {
    return order.customer?.displayName || "Unknown Customer";
  };

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders(prev =>
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId) 
        : [...prev, orderId]
    );
  };

  const getGroupColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: "order-group-blue",
      green: "order-group-green", 
      purple: "order-group-purple",
      orange: "order-group-orange",
      pink: "order-group-pink",
      teal: "order-group-teal",
      indigo: "order-group-indigo",
      amber: "order-group-amber",
    };
    return colors[color] || colors.blue;
  };

  const allGroupedOrderIds = orderGroups.flatMap(group => group.orders);
  const availableOrders = orders.filter(order => !allGroupedOrderIds.includes(order.id));

  const handleCreateGroup = () => {
    if (selectedOrders.length === 0) {
      Swal.fire({
        title: 'No Orders Selected',
        text: 'Please select at least one order to create a group.',
        icon: 'warning',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }

    const groupName = newGroupName || `Group ${orderGroups.length + 1}`;
    const groupColors = ["blue", "green", "purple", "orange", "pink", "teal", "indigo", "amber"];
    const groupColor = groupColors[orderGroups.length % groupColors.length];

    const newGroup: OrderGroup = {
      id: `group-${Date.now()}`,
      name: groupName,
      orders: [...selectedOrders],
      color: groupColor,
    };

    setOrderGroups(prev => [...prev, newGroup]);
    setSelectedOrders([]);
    setNewGroupName("");
    
    Swal.fire({
      title: 'Group Created!',
      text: `Group "${groupName}" has been created with ${selectedOrders.length} orders.`,
      icon: 'success',
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const removeOrderGroup = (groupId: string) => {
    Swal.fire({
      title: 'Remove Group?',
      text: 'Are you sure you want to remove this entire group?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, remove it',
    }).then((result) => {
      if (result.isConfirmed) {
        setOrderGroups(prev => prev.filter(group => group.id !== groupId));
        Swal.fire({
          title: 'Removed!',
          text: 'Group has been removed.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    });
  };

  const removeOrderFromGroup = (groupId: string, orderId: string) => {
    setOrderGroups(prev =>
      prev.map(group => {
        if (group.id === groupId) {
          const updatedOrders = group.orders.filter(id => id !== orderId);
          return updatedOrders.length > 0 
            ? { ...group, orders: updatedOrders } 
            : null;
        }
        return group;
      }).filter(Boolean) as OrderGroup[]
    );
  };

  const handleProceedToGroupedOrders = () => {
    if (orderGroups.length === 0) {
      Swal.fire({
        title: 'No Groups Created',
        text: 'Please create at least one group before proceeding.',
        icon: 'warning',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }
    onProceedToGroupedOrders(orderGroups);
  };

  const getOrderById = (orderId: string) => {
    return orders.find(order => order.id === orderId);
  };

  const calculateOrderTotalItems = (order: Order) => {
    return order.orderItems.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <div className="order-selection-container">
      <button 
        onClick={onBackToCartList}
        className="order-selection-back-button"
      >
        ← Back to Cart List
      </button>

      {orderGroups.length > 0 && (
        <div className="order-groups-section">
          <div className="order-groups-header">
            <Tag className="order-groups-icon" />
            <h4 className="order-groups-title">
              Created Order Groups ({orderGroups.length})
            </h4>
          </div>

          <div className="order-groups-list">
            {orderGroups.map(group => (
              <div 
                key={group.id} 
                className={`order-group-card ${getGroupColor(group.color)}`}
              >
                <div className="order-group-header">
                  <div className="order-group-info">
                    <Tag className="order-group-tag-icon" />
                    <span className="order-group-name">{group.name}</span>
                    <span className="order-group-count">
                      ({group.orders.length} orders)
                    </span>
                  </div>
                  <button
                    onClick={() => removeOrderGroup(group.id)}
                    className="order-group-remove-button"
                    title="Remove entire group"
                  >
                    <X className="order-group-remove-icon" />
                  </button>
                </div>

                <div className="order-group-orders">
                  {group.orders.map(orderId => {
                    const order = getOrderById(orderId);
                    return order ? (
                      <div key={orderId} className="order-in-group">
                        <div className="order-in-group-info">
                          <div className="order-in-group-id">{order.orderReference}</div>
                          <div className="order-in-group-customer">
                            {getCustomerDisplayName(order)}
                          </div>
                          <div className="order-in-group-items">
                            {calculateOrderTotalItems(order)} items
                          </div>
                        </div>
                        <button
                          onClick={() => removeOrderFromGroup(group.id, orderId)}
                          className="order-in-group-remove"
                          title="Remove from group"
                        >
                          <X className="order-in-group-remove-icon" />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="order-groups-actions">
            <button
              onClick={handleProceedToGroupedOrders}
              disabled={orderGroups.length === 0}
              className={`order-groups-proceed-button ${
                orderGroups.length === 0 ? "order-groups-proceed-button-disabled" : ""
              }`}
            >
              <CheckCircle className="order-groups-proceed-icon" />
              Proceed with Groups ({orderGroups.length})
            </button>
          </div>
        </div>
      )}

      {availableOrders.length > 0 && (
        <div className="available-orders-section">
          <div className="available-orders-header">
            <h4 className="available-orders-title">
              Select Orders to Create New Group
            </h4>
            <p className="available-orders-subtitle">
              Select orders below and click "Add to New Group" to create a group
            </p>
          </div>

          <div className="new-group-creation">
            <div className="new-group-input-container">
              <input
                type="text"
                placeholder="Enter group name (optional)"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="new-group-input"
              />
              <span className="new-group-default">
                Default: Group {orderGroups.length + 1}
              </span>
            </div>
          </div>

          <div className="available-orders-grid">
            {availableOrders.map(order => {
              const isSelected = selectedOrders.includes(order.id);
              return (
                <div
                  key={order.id}
                  onClick={() => toggleOrderSelection(order.id)}
                  className={`available-order-card ${
                    isSelected ? "available-order-card-selected" : ""
                  }`}
                >
                  <div className="available-order-header">
                    <div className="available-order-info">
                      <h5 className="available-order-id">{order.orderReference}</h5>
                    </div>
                    <input 
                      type="checkbox" 
                      checked={isSelected} 
                      readOnly 
                      className="available-order-checkbox" 
                    />
                  </div>

                  <div className="available-order-items">
                    {order.orderItems.map((item, index) => (
                      <div key={index} className="available-order-item">
                        • {item.displayName}: {item.quantity} items
                        {item.portionSize && ` (${item.portionSize})`}
                      </div>
                    ))}
                  </div>

                  <div className="available-order-footer">
                    <div className="available-order-total">
                      Total: LKR {order.paymentSummary.totalPrice || 0}
                    </div>
                    <div className="available-order-status">
                      Status: {order.orderStatus}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={handleCreateGroup}
            disabled={selectedOrders.length === 0}
            className={`create-group-button ${
              selectedOrders.length === 0 ? "create-group-button-disabled" : ""
            }`}
          >
            <Package className="create-group-icon" />
            Add to New Group ({selectedOrders.length} orders)
          </button>
        </div>
      )}

      {availableOrders.length === 0 && orderGroups.length === 0 && (
        <div className="no-orders-available">
          <Package className="no-orders-icon" />
          <p className="no-orders-text">No orders available to select</p>
        </div>
      )}
    </div>
  );
};

export default OrderSelection;